/**
 * Shared typography values, semantic aliases, and component mixins.
 * GlobalStyles leaves the root font size at the browser default.
 */

// ============================================================================
// Size scale
// ============================================================================

/**
 * The px-prefixed names are historical; values are rem units, not pixels.
 * For example, px10 is 16px when the browser root font size is 16px.
 */
export const scale = {
  px10: '1rem',
  px12: '1.2rem',
  px14: '1.4rem',
  px16: '1.6rem',
  px18: '1.8rem',
  px20: '2.0rem',
  px24: '2.4rem',
  px28: '2.8rem',
  px32: '3.2rem',
  px34: '3.4rem',
  px48: '4.8rem',
  px50: '5.0rem',
  px64: '6.4rem',
} as const;

// ============================================================================
// Font Families
// ============================================================================

export const fontFamilies = {
  /** Serif fonts for titles/headings in minimal theme */
  serif: "ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif",

  /** Georgia serif for classic theme body content */
  serifClassic: 'Georgia, serif',

  /** Monospace fonts for body text in minimal theme */
  monospace: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',

  /** Sans-serif for UI elements and classic theme */
  sansSerif:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
} as const;

// ============================================================================
// Hero text font (self-hosted in static/fonts, see GlobalStyles @font-face)
// ============================================================================

export const heroFontFamilies = {
  fragmentMono:
    "'Fragment Mono', 'Fragment Mono Fallback', 'Courier New', monospace",
  /** 10-glyph subset of JetBrains Mono. Every glyph is exactly 0.6em wide. */
  banner: "'JetBrains Mono Banner', ui-monospace, Menlo, Consolas, monospace",
} as const;

/** Font files and metric-matched fallbacks. Percentages come from hhea metrics. */
export const heroFontFaces = [
  {
    family: 'Fragment Mono',
    file: 'fragment-mono.woff2',
    weight: '400',
    style: 'normal',
    fallback: { local: 'Courier New', size: 103, ascent: 92.2, descent: 24.3 },
  },
  {
    // `block` hides the banner until the font loads, so a system font with
    // different glyph widths never draws (and clips) the box-drawing rows.
    family: 'JetBrains Mono Banner',
    file: 'jetbrains-mono-banner.woff2',
    weight: '400',
    style: 'normal',
    display: 'block',
  },
] as const;

export const hero = {
  /** Box-drawing glyphs need a font that has them; Fragment Mono does not. */
  bannerFamily: heroFontFamilies.banner,
  textFamily: heroFontFamilies.fragmentMono,
  /** Banner font is 0.6em; the slack covers system fonts if it fails to load. */
  bannerGlyphWidthEm: 0.62,
  bannerMaxSize: '0.875rem',
  bannerLeading: '1.05',
  textSize: ['0.875rem', '0.8125rem'] as const,
  textLeading: '1.7',
  /** Files preloaded in gatsby-ssr. */
  preload: ['fragment-mono.woff2', 'jetbrains-mono-banner.woff2'] as const,
} as const;

/** Set false to turn off the hero entrance animation. */
export const heroMotionEnabled = true;

// ============================================================================
// Semantic Aliases (Theme-Separated)
// ============================================================================

/** Semantic sizes for shared, minimal, and classic typography. */
export const semantic = {
  body: scale.px16,
  caption: scale.px12,

  minimal: {
    display: {
      xl: scale.px50,
      lg: scale.px34,
      md: scale.px14,
      sm: scale.px12,
    },
    heading: {
      featured: scale.px14,
      regular: scale.px12,
    },
    body: {
      base: scale.px10,
      small: '0.8rem',
    },
    ui: {
      meta: '0.8rem',
      tag: scale.px10,
    },
  },

  classic: {
    display: {
      xl: scale.px50,
      lg: scale.px28,
      md: scale.px20,
      sm: scale.px14,
    },
    heading: {
      h1: scale.px28,
      h2: scale.px24,
      h3: scale.px20,
      h4: scale.px16,
      h5: scale.px14,
      h6: scale.px14,
      card: scale.px12,
    },
    body: {
      xl: scale.px14,
      lg: scale.px14,
      md: scale.px12,
    },
    ui: {
      lg: scale.px12,
      md: scale.px10,
    },
  },

  code: {
    inline: '0.85em', // Relative to the parent text size.
    block: scale.px10,
    table: scale.px14,
  },
} as const;

// ============================================================================
// Breakpoints
// ============================================================================

export const breakpoints = {
  mobile: '500px',
  tablet: '768px',
  desktop: '1024px',
  wide: '1200px',
} as const;

/** Social post overlay sizes. Pixel equivalents below assume a 16px root. */
export const social = {
  fontFamily: fontFamilies.sansSerif,
  body: '0.9375rem', // 15px
  name: '0.9375rem', // 15px
  handle: '0.9375rem', // 15px
  meta: '0.8125rem', // 13px
  avatar: '2.25rem', // 36px
  avatarCompact: '1.75rem', // 28px
  lineHeight: '1.35',
  measure: '620px',
} as const;

// ============================================================================
// Responsive Font Sizes
// ============================================================================

/**
 * Responsive size mappings using semantic tokens.
 * Mobile sizes use scale primitives for consistency.
 */
export const responsiveFontSizes = {
  display: {
    xl: {
      desktop: semantic.classic.display.xl,
      mobile: scale.px34,
    },
    lg: {
      desktop: semantic.classic.display.lg,
      tablet: scale.px32,
      mobile: scale.px24,
    },
  },

  heading: {
    h1: {
      desktop: semantic.classic.heading.h1,
      mobile: scale.px24,
    },
    h2: {
      desktop: semantic.classic.heading.h2,
      mobile: scale.px20,
    },
    h3: {
      desktop: semantic.classic.heading.h3,
      mobile: scale.px18,
    },
    h4: {
      desktop: semantic.classic.heading.h4,
      mobile: scale.px16,
    },
    h5: {
      desktop: semantic.classic.heading.h5,
      mobile: scale.px16,
    },
    h6: {
      desktop: semantic.classic.heading.h6,
      mobile: scale.px14,
    },
    card: {
      desktop: semantic.classic.heading.card,
      mobile: scale.px14,
    },
  },

  body: {
    lg: {
      desktop: semantic.classic.body.lg,
      mobile: scale.px12,
    },
    md: {
      desktop: semantic.classic.body.md,
      mobile: scale.px10,
    },
  },
} as const;

// ============================================================================
// Line Heights
// ============================================================================

export const lineHeights = {
  tight: '1.2',
  snug: '1.25',
  normal: '1.5',
  relaxed: '1.6',
  loose: '2',
} as const;

// ============================================================================
// Font Weights
// ============================================================================

export const fontWeights = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

// ============================================================================
// Backward Compatibility Layer
// ============================================================================

/** Compatibility aliases for existing component imports. */
export const fontSizes = {
  display: {
    xl: semantic.classic.display.xl,
    lg: semantic.classic.display.lg,
    md: semantic.classic.display.md,
    sm: semantic.classic.display.sm,
  },
  heading: {
    h1: semantic.classic.heading.h1,
    h2: semantic.classic.heading.h2,
    h3: semantic.classic.heading.h3,
    h4: semantic.classic.heading.h4,
    h5: semantic.classic.heading.h5,
    h6: semantic.classic.heading.h6,
    card: semantic.classic.heading.card,
    postFeatured: semantic.minimal.heading.featured,
    postRegular: semantic.minimal.heading.regular,
    large: scale.px32,
  },
  body: {
    xl: semantic.classic.body.xl,
    lg: semantic.classic.body.lg,
    md: semantic.classic.body.md,
    base: semantic.minimal.body.base,
    sm: semantic.minimal.body.small,
  },
  ui: {
    lg: semantic.classic.ui.lg,
    md: semantic.classic.ui.md,
    base: semantic.minimal.ui.meta,
    sm: semantic.minimal.ui.meta,
    xs: semantic.minimal.ui.meta,
    xxs: semantic.minimal.ui.meta,
    tiny: semantic.minimal.ui.meta,
  },
  code: {
    inline: semantic.code.inline,
    block: semantic.code.block,
    table: semantic.code.table,
  },
} as const;

// ============================================================================
// Typography style mixins
// ============================================================================

/** CSS mixins built from the semantic sizes above. */
export const typography = {
  // Display styles
  displayXL: `
    font-family: ${fontFamilies.serif};
    font-size: ${semantic.classic.display.xl};
    font-weight: ${fontWeights.semibold};
    line-height: ${lineHeights.tight};

    @media (max-width: ${breakpoints.mobile}) {
      font-size: ${responsiveFontSizes.display.xl.mobile};
    }
  `,

  displayLG: `
    font-family: ${fontFamilies.serif};
    font-size: ${semantic.classic.display.lg};
    font-weight: ${fontWeights.semibold};
    line-height: ${lineHeights.snug};

    @media (max-width: ${breakpoints.desktop}) {
      font-size: ${responsiveFontSizes.display.lg.tablet};
    }

    @media (max-width: ${breakpoints.mobile}) {
      font-size: ${responsiveFontSizes.display.lg.mobile};
    }
  `,

  displayMD: `
    font-family: ${fontFamilies.serif};
    font-size: ${semantic.classic.display.md};
    font-weight: ${fontWeights.bold};
    line-height: ${lineHeights.tight};
  `,

  displaySM: `
    font-family: ${fontFamilies.serif};
    font-size: ${semantic.classic.display.sm};
    font-weight: ${fontWeights.bold};
    line-height: ${lineHeights.tight};
  `,

  // Heading styles (for article content)
  h1: `
    font-family: ${fontFamilies.sansSerif};
    font-size: ${semantic.classic.heading.h1};
    font-weight: ${fontWeights.semibold};
    line-height: ${lineHeights.snug};

    @media (max-width: ${breakpoints.desktop}) {
      font-size: ${responsiveFontSizes.heading.h1.mobile};
    }
  `,

  h2: `
    font-family: ${fontFamilies.sansSerif};
    font-size: ${semantic.classic.heading.h2};
    font-weight: ${fontWeights.semibold};
    line-height: ${lineHeights.snug};

    @media (max-width: ${breakpoints.desktop}) {
      font-size: ${responsiveFontSizes.heading.h2.mobile};
    }
  `,

  h3: `
    font-family: ${fontFamilies.sansSerif};
    font-size: ${semantic.classic.heading.h3};
    font-weight: ${fontWeights.semibold};
    line-height: ${lineHeights.snug};

    @media (max-width: ${breakpoints.desktop}) {
      font-size: ${responsiveFontSizes.heading.h3.mobile};
    }
  `,

  h4: `
    font-family: ${fontFamilies.sansSerif};
    font-size: ${semantic.classic.heading.h4};
    font-weight: ${fontWeights.semibold};
    line-height: ${lineHeights.snug};

    @media (max-width: ${breakpoints.desktop}) {
      font-size: ${responsiveFontSizes.heading.h4.mobile};
    }
  `,

  h5: `
    font-family: ${fontFamilies.sansSerif};
    font-size: ${semantic.classic.heading.h5};
    font-weight: ${fontWeights.semibold};
    line-height: ${lineHeights.snug};

    @media (max-width: ${breakpoints.desktop}) {
      font-size: ${responsiveFontSizes.heading.h5.mobile};
    }
  `,

  h6: `
    font-family: ${fontFamilies.sansSerif};
    font-size: ${semantic.classic.heading.h6};
    font-weight: ${fontWeights.semibold};
    line-height: ${lineHeights.snug};

    @media (max-width: ${breakpoints.desktop}) {
      font-size: ${responsiveFontSizes.heading.h6.mobile};
    }
  `,

  // Minimal theme specific styles
  minimalTitle: `
    font-family: ${fontFamilies.monospace};
    font-weight: ${fontWeights.bold};
    line-height: ${lineHeights.tight};
  `,

  minimalTitleLarge: `
    font-family: ${fontFamilies.monospace};
    font-size: ${semantic.minimal.heading.featured};
    font-weight: ${fontWeights.bold};
    line-height: ${lineHeights.tight};
  `,

  minimalTitleRegular: `
    font-family: ${fontFamilies.monospace};
    font-size: ${semantic.minimal.heading.regular};
    font-weight: ${fontWeights.bold};
    line-height: ${lineHeights.tight};
  `,

  minimalBody: `
    font-family: ${fontFamilies.monospace};
    font-size: ${semantic.minimal.body.base};
    line-height: ${lineHeights.normal};
  `,

  minimalBodySmall: `
    font-family: ${fontFamilies.monospace};
    font-size: ${semantic.minimal.body.small};
    line-height: ${lineHeights.normal};
  `,

  minimalUI: `
    font-family: ${fontFamilies.monospace};
    font-size: ${semantic.minimal.ui.meta};
  `,

  // Classic theme body text
  bodyLarge: `
    font-family: ${fontFamilies.serifClassic};
    font-size: ${semantic.classic.body.lg};
    line-height: ${lineHeights.relaxed};

    @media (max-width: ${breakpoints.desktop}) {
      font-size: ${responsiveFontSizes.body.lg.mobile};
    }
  `,

  bodyBase: `
    font-family: ${fontFamilies.sansSerif};
    font-size: ${semantic.classic.body.xl};
    line-height: ${lineHeights.normal};
  `,

  bodyMedium: `
    font-family: ${fontFamilies.serifClassic};
    font-size: ${semantic.classic.body.md};
    line-height: ${lineHeights.normal};

    @media (max-width: ${breakpoints.desktop}) {
      font-size: ${responsiveFontSizes.body.md.mobile};
    }
  `,

  // UI text styles
  uiLarge: `
    font-family: ${fontFamilies.sansSerif};
    font-size: ${semantic.classic.ui.lg};
  `,

  uiMedium: `
    font-family: ${fontFamilies.monospace};
    font-size: ${semantic.classic.ui.md};
  `,

  uiSmall: `
    font-family: ${fontFamilies.monospace};
    font-size: ${semantic.minimal.ui.meta};
  `,

  // Code styles
  codeInline: `
    font-family: ${fontFamilies.monospace};
    font-size: ${semantic.code.inline};
  `,

  codeBlock: `
    font-family: ${fontFamilies.monospace};
    font-size: ${semantic.code.block};
    line-height: ${lineHeights.normal};
  `,

  // UI Label & Caption styles (brutalist theme)
  /** Uppercase label - for badges, section headers, year dividers */
  label: `
    font-family: ${fontFamilies.monospace};
    font-size: ${semantic.minimal.ui.meta};
    font-weight: ${fontWeights.bold};
    text-transform: uppercase;
    letter-spacing: 0.1em;
  `,

  /** Caption text - for video/image captions */
  caption: `
    font-family: ${fontFamilies.monospace};
    font-size: ${semantic.classic.ui.md};
    line-height: ${lineHeights.normal};
  `,
} as const;

// ============================================================================
// TypeScript Types
// ============================================================================

export type FontFamily = keyof typeof fontFamilies;
export type FontSize = keyof typeof fontSizes;
export type LineHeight = keyof typeof lineHeights;
export type FontWeight = keyof typeof fontWeights;
export type Breakpoint = keyof typeof breakpoints;
